import {useContext, useEffect, useRef, useState} from "react";
import AppContext from "../context/AppContext";
import TagList from "./TagList";
import FavStar from "./FavStar";
import {formatSeconds} from "../lib/static";
import {useNavigate} from "react-router-dom";
import './FilePreview.scss'

type Props = {
    file: VideoFile
    onClick: () => void
    selected?: boolean
    onSelect?: ((selected: boolean) => void) | null
    className?: string
    showDirectory: boolean
    canBeSelected?: boolean
    selectionMode?: boolean
    onContextMenu?: (x: number, y: number) => void
}

export default ({ file, onClick, className, selected = false, onSelect = null, showDirectory = false, canBeSelected = false, selectionMode = false, onContextMenu }: Props) => {
    const { selectedVideo } = useContext(AppContext)
    const navigate = useNavigate()

    const [hover, setHover] = useState(false)
    const [checked, setChecked] = useState(selected)
    const containerRef = useRef<HTMLDivElement>(null)
    const longPressTimer = useRef<number | null>(null)

    const isCircle = file.fileType === 'circle' as any
    const isCurrent = selectedVideo?.hash === file.hash

    useEffect(() => {
        if (onSelect) onSelect(checked)
    }, [checked])

    const onSelectionChange = (e: any) => {
        e.preventDefault()
        e.stopPropagation()
        setChecked(!selected)
    }

    const onFileClick = (ev: any) => {
        ev.preventDefault()
        onClick()
    }

    const onDirClick = (e: any) => {
        e.preventDefault()
        e.stopPropagation()
        navigate(e.currentTarget.getAttribute('href'))
    }

    const handleContextMenu = (e: React.MouseEvent) => {
        if (!onContextMenu) return
        e.preventDefault()
        e.stopPropagation()
        onContextMenu(e.clientX, e.clientY)
    }

    const onTouchStart = (e: React.TouchEvent) => {
        if (!onContextMenu) return
        const touch = e.touches[0]
        const x = touch.clientX
        const y = touch.clientY
        longPressTimer.current = window.setTimeout(() => {
            longPressTimer.current = null
            onContextMenu(x, y)
        }, 500)
    }

    const cancelLongPress = () => {
        if (longPressTimer.current !== null) {
            window.clearTimeout(longPressTimer.current)
            longPressTimer.current = null
        }
    }

    const imgSrc = hover && file.paths.gif ? file.paths.gif : file.paths.thumbnail

    const cardClass = [
        'fp-card',
        isCurrent      ? 'fp-current'       : '',
        selected       ? 'fp-checked'        : '',
        selectionMode  ? 'fp-selection-mode' : '',
        className      || '',
    ].filter(Boolean).join(' ')

    return (
        <div
            className={cardClass}
            ref={containerRef}
            onContextMenu={handleContextMenu}
            onTouchStart={onTouchStart}
            onTouchEnd={cancelLongPress}
            onTouchMove={cancelLongPress}
        >
            {canBeSelected && onSelect && (
                <input className="fp-checkbox" type="checkbox" checked={selected} onChange={onSelectionChange}/>
            )}

            <FavStar file={file}/>
            <TagList video={file} showAdd={true}/>

            <a
                className="fp-link"
                href={file.paths.stream}
                onClick={onFileClick}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            >
                <div className={'fp-thumb' + (isCircle ? ' fp-thumb--circle' : '')}>
                    <div
                        className="fp-img"
                        style={{ backgroundImage: `url(${imgSrc})` }}
                    />
                    {typeof file.meta?.duration !== 'undefined' && (
                        <span className="fp-duration">{formatSeconds(file.meta.duration)}</span>
                    )}
                    <div className="fp-hover-overlay">
                        {file.fileType !== 'photo' && <div className="fp-play-btn">▶</div>}
                    </div>
                </div>

                <div className="fp-meta">
                    {showDirectory && file.directory?.hash && (
                        <a href={`/dir/${file.directory.hash}`} className="fp-dir" onClick={onDirClick}>
                            {file.directory.name}
                        </a>
                    )}
                    <div className="fp-name">{file.name}</div>
                </div>
            </a>
        </div>
    )
}
